import { Link } from 'react-router-dom'
import { AlertTriangle, ExternalLink, ImageOff, Layers, Loader2, Play } from 'lucide-react'
import { cn } from '@orisini/ui/lib/utils'
import { formatDuration, PRODUCTION_LABEL, PUBLISH_LABEL, when, type ContentItem, type ProductionStatus, type PublishStatus } from '../lib/content'

const WARNA_PROD: Record<ProductionStatus, string> = {
  drafting: 'bg-muted text-muted-foreground',
  draft: 'bg-sky-500/15 text-sky-300',
  approved: 'bg-muted text-muted-foreground',
  producing: 'bg-muted text-muted-foreground',
  ready: 'bg-emerald-500/15 text-emerald-400',
  failed: 'bg-destructive/15 text-destructive',
  rejected: 'bg-muted text-muted-foreground',
}

const WARNA_PUB: Record<PublishStatus, string> = {
  none: 'bg-muted text-muted-foreground',
  awaiting_approval: 'bg-amber-500/15 text-amber-400',
  queued: 'bg-sky-500/15 text-sky-300',
  scheduled: 'bg-sky-500/15 text-sky-300',
  published: 'bg-emerald-500/15 text-emerald-400',
  failed: 'bg-destructive/15 text-destructive',
}

const SIBUK: ProductionStatus[] = ['drafting', 'approved', 'producing']

export function StatusBadges({ item }: { item: Pick<ContentItem, 'productionStatus' | 'publishStatus' | 'scheduledAt'> }) {
  return (
    <span className="flex flex-wrap gap-1.5">
      <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium', WARNA_PROD[item.productionStatus])}>
        {SIBUK.includes(item.productionStatus) && <Loader2 className="size-3 animate-spin" />}
        {PRODUCTION_LABEL[item.productionStatus]}
      </span>
      {item.productionStatus === 'ready' && (
        <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', WARNA_PUB[item.publishStatus])}>
          {PUBLISH_LABEL[item.publishStatus]}
          {item.publishStatus === 'scheduled' && item.scheduledAt ? ` · ${when(item.scheduledAt)}` : ''}
        </span>
      )}
    </span>
  )
}

/**
 * Daftar konten untuk Review dan Content Bank. Aksi per baris datang dari
 * halaman pemanggil (`actions`), karena tiap halaman punya keputusannya sendiri.
 */
export function ContentList({
  items,
  picked,
  onToggle,
  actions,
}: {
  items: ContentItem[]
  picked?: Set<string>
  onToggle?: (id: string) => void
  actions: (item: ContentItem) => React.ReactNode
}) {
  return (
    <ul className="space-y-3">
      {items.map((it) => (
        <li
          key={it.id}
          className={cn('flex gap-3 rounded-lg border p-3', picked?.has(it.id) ? 'border-primary bg-primary/5' : 'border-border')}
        >
          {onToggle && (
            <input
              type="checkbox"
              className="mt-1 size-4 shrink-0"
              checked={picked?.has(it.id) ?? false}
              onChange={() => onToggle(it.id)}
              aria-label={`Choose "${it.title}"`}
            />
          )}
          <Link
            to={`/content/${it.id}`}
            className="relative flex h-24 w-[4.8rem] shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted"
          >
            {it.thumbUrl ? (
              <img src={it.thumbUrl} alt="" loading="lazy" className="size-full object-cover" />
            ) : (
              <ImageOff className="size-4 text-muted-foreground" />
            )}
            {it.kind === 'video' && (
              <span className="absolute right-1 top-1 flex items-center gap-0.5 rounded bg-black/60 px-1 text-[10px] text-white">
                <Play className="size-3" /> {it.durationSec ? formatDuration(it.durationSec) : ''}
              </span>
            )}
            {it.kind === 'carousel' && it.fileCount > 1 && (
              <span className="absolute right-1 top-1 flex items-center gap-0.5 rounded bg-black/60 px-1 text-[10px] text-white">
                <Layers className="size-3" /> {it.fileCount}
              </span>
            )}
          </Link>
          <div className="min-w-0 flex-1 space-y-1">
            <Link to={`/content/${it.id}`} className="line-clamp-2 font-medium hover:underline">
              {it.title}
            </Link>
            <p className="text-xs text-muted-foreground">
              {[
                it.workspaceName,
                it.platform,
                it.kind === 'poster' ? 'Poster' : `${it.kind === 'video' ? 'Video' : 'Carousel'}${it.style ? ` · ${it.style}` : ''}`,
                it.kind === 'video' && it.durationSec ? formatDuration(it.durationSec) : null,
                it.kind === 'video' && it.renderLocation === 'agent' && it.productionStatus === 'producing' ? 'rendering on your computer' : null,
                when(it.updatedAt),
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
            <StatusBadges item={it} />
            {it.error && (
              <p className="flex items-start gap-1 text-xs text-destructive">
                <AlertTriangle className="mt-0.5 size-3 shrink-0" /> {it.error}
              </p>
            )}
            {it.publishedUrl && (
              <a href={it.publishedUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                View on {it.platform ?? 'the platform'} <ExternalLink className="size-3" />
              </a>
            )}
          </div>
          <div className="flex shrink-0 flex-col items-end justify-center gap-1">{actions(it)}</div>
        </li>
      ))}
    </ul>
  )
}
